/**
 * Company Profile Store
 *
 * Store Zustand untuk menyimpan data Company Profile.
 * Berfungsi sebagai "variabel public" yang dapat diakses di seluruh aplikasi.
 *
 * PENTING: Nama variabel disamakan dengan Booku Lama (mdl_VariabelUmum.vb:31-68)
 * untuk memudahkan komparasi dan konsistensi.
 *
 * Penggunaan:
 *   const { NamaPerusahaan, PerusahaanSebagaiPKP } = useCompanyStore();
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type { CompanyProfile, CompanyBasicInfo } from '@/modules/file/types';
import companyService from '@/modules/file/companyService';
import { useAuthStore } from '@/modules/auth/authStore';

// ============================================
// KONSTANTA (sama dengan Booku Lama)
// ============================================
export const JenisPerusahaan_IndustriManufactur = 'Industri/Manufaktur';
export const JenisPerusahaan_Dagang = 'Dagang';
export const JenisPerusahaan_Jasa = 'Jasa';
export const SistemCOA_StandarAplikasi = 'Standar Aplikasi';
export const SistemCOA_Customize = 'Customize';

interface CompanyState {
  // Data Company Profile lengkap (internal)
  company: CompanyProfile | null;

  // Loading state
  isLoading: boolean;

  // Actions
  setCompany: (company: CompanyProfile) => void;
  updateCompany: (updates: Partial<CompanyProfile>) => void;
  clearCompany: () => void;
  setLoading: (loading: boolean) => void;

  /**
   * Fetch Company Profile dari server lalu populate store + derived fields.
   *
   * Padanan Booku Lama: `IsiValueCompanyProfile` yang dipanggil saat login dan
   * setelah user update Company Profile. Di V2, hook ini dipanggil di App.tsx
   * saat user menjadi `isAuthenticated` (lihat App.tsx — useEffect).
   *
   * Catatan: hanya fetch sekali per session — tidak refresh otomatis untuk
   * external DB change. Untuk guard kritikal yang harus selalu pakai fresh value,
   * gunakan `companyService.get()` langsung di point-of-use (lihat
   * BukuPengawasanKetetapanPajakPage.handleInput).
   *
   * Return value: true kalau sukses, false kalau gagal.
   */
  loadProfile: () => Promise<boolean>;

  // ============================================
  // VARIABEL PUBLIC COMPANY PROFILE
  // Nama variabel disamakan dengan Booku Lama
  // (mdl_VariabelUmum.vb:31-68)
  // ============================================

  // --- Identitas ---
  ID_Customer: string;
  NomorSeriProduk: string;

  // --- Informasi Dasar ---
  NamaPerusahaan: string;
  TaglinePerusahaan: string;
  AlamatPerusahaan: string;
  EmailPerusahaan: string;
  PICPerusahaan: string;

  // --- Jenis Usaha ---
  SebagaiPerusahaanDagang: boolean;
  SebagaiPerusahaanManufaktur: boolean;
  SebagaiPerusahaanJasa: boolean;

  // --- Data Perpajakan ---
  JenisWPPerusahaan: string;
  NPWPPerusahaan: string;
  NamaDirekturPerusahaan: string;
  NomorSKTPerusahaan: string;
  TanggalSKTPerusahaan: string | null;
  KodeKPP_Perusahaan: string;
  PerusahaanSebagaiPemotongPPh: boolean;

  // --- Akses DJP Online ---
  EmailDJPO_Perusahaan: string;
  PasswordDJPO_Perusahaan: string;

  // --- UMKM ---
  NomorSuketUMKM_Perusahaan: string;
  TanggalSuketUMKM_Perusahaan: string | null;
  PerusahaanSebagaiUMKM: boolean;

  // --- PKP & e-Faktur ---
  TanggalPKP_Perusahaan: string | null;
  PerusahaanSebagaiPKP: boolean;
  TanggalExpireSEPerusahaan: string | null;
  Password_eFaktur_Perusahaan: string;
  KodeAktivasiPerusahaan: string;
  PassphrasePerusahaan: string;

  // --- Jasa Konstruksi ---
  LevelPJK_Perusahaan: number;
  TanggalExpireSBUPerusahaan: string | null;
  PerusahaanSebagaiJasaKonstruksi: boolean;

  // --- Pengaturan ---
  SistemPenomoranInvoicePenjualan_Perusahaan: string;
  /**
   * Penanda lock Sistem Penomoran Invoice Penjualan (dari server).
   * Locked permanen setelah Invoice Penjualan pertama terbit.
   * Booku V2 only — Booku Lama belum memiliki mekanisme lock ini.
   */
  SistemPenomoranInvoiceLocked: boolean;
  SistemApprovalPerusahaan: boolean;
  SistemCOA: string;

  // --- Tanggal Expire Aplikasi ---
  TanggalExpireApp: string | null;

  // ============================================
  // HELPER FUNCTIONS
  // ============================================
  getBasicInfo: () => CompanyBasicInfo;
}

// ============================================
// HELPER: Compute derived values from company
// ============================================

interface CompanyVariables {
  ID_Customer: string;
  NomorSeriProduk: string;
  NamaPerusahaan: string;
  TaglinePerusahaan: string;
  AlamatPerusahaan: string;
  EmailPerusahaan: string;
  PICPerusahaan: string;
  SebagaiPerusahaanDagang: boolean;
  SebagaiPerusahaanManufaktur: boolean;
  SebagaiPerusahaanJasa: boolean;
  JenisWPPerusahaan: string;
  NPWPPerusahaan: string;
  NamaDirekturPerusahaan: string;
  NomorSKTPerusahaan: string;
  TanggalSKTPerusahaan: string | null;
  KodeKPP_Perusahaan: string;
  PerusahaanSebagaiPemotongPPh: boolean;
  EmailDJPO_Perusahaan: string;
  PasswordDJPO_Perusahaan: string;
  NomorSuketUMKM_Perusahaan: string;
  TanggalSuketUMKM_Perusahaan: string | null;
  PerusahaanSebagaiUMKM: boolean;
  TanggalPKP_Perusahaan: string | null;
  PerusahaanSebagaiPKP: boolean;
  TanggalExpireSEPerusahaan: string | null;
  Password_eFaktur_Perusahaan: string;
  KodeAktivasiPerusahaan: string;
  PassphrasePerusahaan: string;
  LevelPJK_Perusahaan: number;
  TanggalExpireSBUPerusahaan: string | null;
  PerusahaanSebagaiJasaKonstruksi: boolean;
  SistemPenomoranInvoicePenjualan_Perusahaan: string;
  SistemPenomoranInvoiceLocked: boolean;
  SistemApprovalPerusahaan: boolean;
  SistemCOA: string;
  TanggalExpireApp: string | null;
}

function computeVariables(company: CompanyProfile | null): CompanyVariables {
  if (!company) {
    // Return default values when company is null
    return {
      // Identitas
      ID_Customer: '',
      NomorSeriProduk: '',

      // Informasi Dasar
      NamaPerusahaan: '',
      TaglinePerusahaan: '',
      AlamatPerusahaan: '',
      EmailPerusahaan: '',
      PICPerusahaan: '',

      // Jenis Usaha
      SebagaiPerusahaanDagang: false,
      SebagaiPerusahaanManufaktur: false,
      SebagaiPerusahaanJasa: false,

      // Data Perpajakan
      JenisWPPerusahaan: '',
      NPWPPerusahaan: '',
      NamaDirekturPerusahaan: '',
      NomorSKTPerusahaan: '',
      TanggalSKTPerusahaan: null,
      KodeKPP_Perusahaan: '',
      PerusahaanSebagaiPemotongPPh: false,

      // Akses DJP Online
      EmailDJPO_Perusahaan: '',
      PasswordDJPO_Perusahaan: '',

      // UMKM
      NomorSuketUMKM_Perusahaan: '',
      TanggalSuketUMKM_Perusahaan: null,
      PerusahaanSebagaiUMKM: false,

      // PKP & e-Faktur
      TanggalPKP_Perusahaan: null,
      PerusahaanSebagaiPKP: false,
      TanggalExpireSEPerusahaan: null,
      Password_eFaktur_Perusahaan: '',
      KodeAktivasiPerusahaan: '',
      PassphrasePerusahaan: '',

      // Jasa Konstruksi
      LevelPJK_Perusahaan: 0,
      TanggalExpireSBUPerusahaan: null,
      PerusahaanSebagaiJasaKonstruksi: false,

      // Pengaturan
      SistemPenomoranInvoicePenjualan_Perusahaan: 'Otomatis',
      SistemPenomoranInvoiceLocked: false,
      SistemApprovalPerusahaan: false,
      SistemCOA: SistemCOA_StandarAplikasi,

      // Tanggal Expire Aplikasi
      TanggalExpireApp: null
    };
  }

  // Compute derived boolean values
  const perusahaanSebagaiUMKM = !!(company.nomorSuketUMKM && company.nomorSuketUMKM.length > 0);
  const perusahaanSebagaiPKP = !!(company.tanggalPKP && company.tanggalPKP.length > 0);
  const perusahaanSebagaiJasaKonstruksi = (company.levelPJK ?? 0) > 0;

  return {
    // Identitas
    ID_Customer: company.idCustomer ?? '',
    NomorSeriProduk: company.nomorSeriProduk ?? '',

    // Informasi Dasar
    NamaPerusahaan: company.namaPerusahaan ?? '',
    TaglinePerusahaan: company.tagline ?? '',
    AlamatPerusahaan: company.alamat ?? '',
    EmailPerusahaan: company.email ?? '',
    PICPerusahaan: company.pic ?? '',

    // Jenis Usaha
    SebagaiPerusahaanDagang: company.sebagaiDagang ?? false,
    SebagaiPerusahaanManufaktur: company.sebagaiManufaktur ?? false,
    SebagaiPerusahaanJasa: company.sebagaiJasa ?? false,

    // Data Perpajakan
    JenisWPPerusahaan: company.jenisWP ?? '',
    NPWPPerusahaan: company.npwp ?? '',
    NamaDirekturPerusahaan: company.namaDirektur ?? '',
    NomorSKTPerusahaan: company.nomorSKT ?? '',
    TanggalSKTPerusahaan: company.tanggalSKT ?? null,
    KodeKPP_Perusahaan: company.kodeKPP ?? '',
    PerusahaanSebagaiPemotongPPh: company.pemotongPPh ?? false,

    // Akses DJP Online
    EmailDJPO_Perusahaan: company.emailDJPO ?? '',
    PasswordDJPO_Perusahaan: company.passwordDJPO ?? '',

    // UMKM
    NomorSuketUMKM_Perusahaan: company.nomorSuketUMKM ?? '',
    TanggalSuketUMKM_Perusahaan: company.tanggalSuketUMKM ?? null,
    PerusahaanSebagaiUMKM: perusahaanSebagaiUMKM,

    // PKP & e-Faktur
    TanggalPKP_Perusahaan: company.tanggalPKP ?? null,
    PerusahaanSebagaiPKP: perusahaanSebagaiPKP,
    TanggalExpireSEPerusahaan: company.tanggalExpireSE ?? null,
    Password_eFaktur_Perusahaan: company.passwordEFaktur ?? '',
    KodeAktivasiPerusahaan: company.kodeAktivasi ?? '',
    PassphrasePerusahaan: company.passphrase ?? '',

    // Jasa Konstruksi
    LevelPJK_Perusahaan: company.levelPJK ?? 0,
    TanggalExpireSBUPerusahaan: company.tanggalExpireSBU ?? null,
    PerusahaanSebagaiJasaKonstruksi: perusahaanSebagaiJasaKonstruksi,

    // Pengaturan
    SistemPenomoranInvoicePenjualan_Perusahaan: company.sistemPenomoranInvoicePenjualan ?? 'Otomatis',
    SistemPenomoranInvoiceLocked: company.sistemPenomoranInvoiceLocked ?? false,
    SistemApprovalPerusahaan: false, // TODO: Add to CompanyProfile type when needed
    SistemCOA: SistemCOA_StandarAplikasi, // TODO: Add to CompanyProfile type when needed

    // Tanggal Expire Aplikasi
    TanggalExpireApp: company.tanggalExpireApp ?? null
  };
}

/**
 * Sync subset Company Profile yang relevan ke `useAuthStore.tenant`.
 *
 * Latar belakang: `useAuthStore.tenant` di-populated saat login dengan subset
 * (name/npwp/alamat/jenis-usaha). Header.tsx & CompanyPopover.tsx baca dari sini.
 * Kalau user update Company Profile, tanpa sync ini Header/Popover tampil
 * value lama sampai re-login. Sync ini diam-diam update authStore.tenant
 * supaya konsumen-konsumen tersebut ikut refresh.
 */
function syncAuthStoreTenant(company: CompanyProfile | null): void {
  if (!company) return;
  const authState = useAuthStore.getState();
  const existingTenant = authState.tenant;
  if (!existingTenant) return; // belum login — skip

  // Sebagian field ikut dari company, sisanya (code) dari tenant lama.
  authState.setTenant({
    code: existingTenant.code,
    name: company.namaPerusahaan ?? existingTenant.name ?? '',
    npwp: company.npwp ?? '',
    alamat: company.alamat ?? '',
    sebagaiPerusahaanDagang: company.sebagaiDagang ?? false,
    sebagaiPerusahaanManufaktur: company.sebagaiManufaktur ?? false,
    sebagaiPerusahaanJasa: company.sebagaiJasa ?? false,
  });
}

export const useCompanyStore = create<CompanyState>()(
  persist(
    (set, get) => ({
      company: null,
      isLoading: false,

      // Default values for all variables
      ...computeVariables(null),

      // Actions
      setCompany: (company) => {
        set({
          company,
          ...computeVariables(company)
        });
        syncAuthStoreTenant(company);
      },

      updateCompany: (updates) =>
        set((state) => {
          const updatedCompany = state.company ? { ...state.company, ...updates } : null;
          syncAuthStoreTenant(updatedCompany);
          return {
            company: updatedCompany,
            ...computeVariables(updatedCompany)
          };
        }),

      clearCompany: () =>
        set({
          company: null,
          ...computeVariables(null)
        }),

      setLoading: (loading) => set({ isLoading: loading }),

      /**
       * Fetch Company Profile dari server lalu populate store.
       * Backend response shape: { success, message, data: CompanyProfile } — UNWRAP `.data` dulu.
       * Tipe return: boolean — true kalau sukses, false kalau gagal.
       * Idempotent: aman dipanggil berkali-kali.
       */
      loadProfile: async () => {
        try {
          const response = await companyService.get();
          if (!response.success || !response.data) {
            console.error('loadProfile gagal:', response.message);
            return false;
          }
          // Pakai action setCompany supaya sync ke authStore juga otomatis.
          get().setCompany(response.data);
          return true;
        } catch (err) {
          console.error('Gagal memuat Company Profile:', err);
          return false;
        }
      },

      // Helper: Data ringkas untuk header/sidebar
      getBasicInfo: () => {
        const company = get().company;
        return {
          namaPerusahaan: company?.namaPerusahaan ?? '',
          tagline: company?.tagline ?? '',
          logoUrl: company?.logoUrl ?? null,
          npwp: company?.npwp ?? '',
          alamat: company?.alamat ?? ''
        };
      }
    }),
    {
      name: 'booku-company-storage',
      partialize: (state) => ({ company: state.company }),
      /**
       * Fix Bug Rehydrate (14-05-2026):
       * `partialize` hanya menyimpan `company` mentah ke localStorage. Saat
       * persist rehydrate, hanya `company` yang di-restore — derived fields
       * (KodeKPP_Perusahaan, PerusahaanSebagaiPKP, dll.) tetap default `""`
       * karena `computeVariables(null)` di initial state sudah dievaluasi.
       *
       * `merge` callback ini menggabungkan persisted state dengan current state,
       * lalu RE-COMPUTE derived fields dari `company` yang baru di-restore.
       * Tanpa ini, setiap reload halaman menyebabkan field-field derived kosong
       * meskipun `company` ter-restore.
       */
      merge: (persistedState, currentState) => {
        const persisted = persistedState as { company?: CompanyProfile | null } | null;
        const restoredCompany = persisted?.company ?? null;
        return {
          ...currentState,
          ...(persisted ?? {}),
          company: restoredCompany,
          ...computeVariables(restoredCompany),
        };
      },
    }
  )
);

// ============================================
// HELPER HOOKS untuk akses mudah
// ============================================

/**
 * Hook untuk mendapatkan data perusahaan dasar.
 *
 * ⚠️ **WAJIB pakai `useShallow`.** `getBasicInfo()` menyusun objek BARU setiap kali
 * dipanggil. Sejak zustand v5 (berbasis `useSyncExternalStore`), selector yang
 * mengembalikan identitas baru tiap render membuat React melempar
 * *"The result of getSnapshot should be cached to avoid an infinite loop"* →
 * render berulang tanpa henti → **halaman blank tanpa error build**.
 *
 * `useShallow` membandingkan isi objek secara dangkal, sehingga referensi baru
 * dengan isi sama tidak memicu render ulang.
 *
 * Terbukti di lapangan 21-08-2026: pemakaian pertama hook ini (tombol Cetak Invoice
 * Penjualan) langsung membuat halaman Invoice Penjualan blank — modalnya selalu
 * ter-mount, jadi hook ikut jalan begitu halaman dibuka.
 *
 * Berlaku untuk selector apa pun yang mengembalikan objek/array — bukan hanya di sini.
 */
export function useCompanyBasicInfo(): CompanyBasicInfo {
  return useCompanyStore(useShallow((state) => state.getBasicInfo()));
}

/**
 * Hook untuk cek status PKP
 * Menggunakan nama yang sama dengan Booku Lama
 */
export function usePerusahaanSebagaiPKP(): boolean {
  return useCompanyStore((state) => state.PerusahaanSebagaiPKP);
}

/**
 * Hook untuk cek status UMKM
 * Menggunakan nama yang sama dengan Booku Lama
 */
export function usePerusahaanSebagaiUMKM(): boolean {
  return useCompanyStore((state) => state.PerusahaanSebagaiUMKM);
}

/**
 * Hook untuk mendapatkan nama perusahaan
 * Menggunakan nama yang sama dengan Booku Lama
 */
export function useNamaPerusahaan(): string {
  return useCompanyStore((state) => state.NamaPerusahaan);
}

/**
 * Hook untuk cek status Jasa Konstruksi
 * Menggunakan nama yang sama dengan Booku Lama
 */
export function usePerusahaanSebagaiJasaKonstruksi(): boolean {
  return useCompanyStore((state) => state.PerusahaanSebagaiJasaKonstruksi);
}
